import {
  Bell,
  Camera,
  ChevronDown,
  Grid2X2,
  HardDrive,
  Moon,
  Monitor,
  Search,
  Server,
  Settings,
  SquareDashed,
  Sun,
  UserRound,
  Video,
  Waves,
} from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMonitoring } from '../context/MonitoringContext';
import { useTheme } from '../context/ThemeContext';
import { formatRole } from '../utils/format';
import { BrandMark } from './BrandMark';
import { ConnectionBadge } from './ConnectionBadge';

const desktopNav = [
  { to: '/devices', label: 'Устройства', icon: HardDrive },
  { to: '/cameras', label: 'Камеры', icon: Video },
  { to: '/servers', label: 'Серверы', icon: Server },
  { to: '/computers', label: 'Компьютеры', icon: Monitor },
  { to: '/events', label: 'События', icon: Bell },
  { to: '/telemetry', label: 'Телеметрия', icon: Waves },
  { to: '/settings', label: 'Настройки', icon: Settings },
];

const mobileNav = [
  { to: '/cameras', label: 'Камеры', icon: Camera },
  { to: '/events', label: 'События', icon: Bell },
  { to: '/devices', label: 'Устройства', icon: HardDrive },
  { to: '/profile', label: 'Профиль', icon: UserRound },
];

// The compact switcher is intentionally limited to the four primary work areas.
// Secondary pages remain available from their existing links and from direct URLs.
const topSwitcherNav = [
  { to: '/cameras', label: 'Камеры', icon: Camera },
  { to: '/devices', label: 'Устройства', icon: HardDrive },
  { to: '/contour', label: 'Контур', icon: SquareDashed },
  { to: '/settings', label: 'Настройки', icon: Settings },
];

const initials = (value?: string) =>
  (value || 'VA')
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();

export function AppShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { profile } = useAuth();
  const { connectionState, snapshot } = useMonitoring();
  const { resolvedTheme, toggleTheme } = useTheme();
  const [search, setSearch] = useState('');
  const openEvents = snapshot?.events.filter((event) => !event.acknowledged).length ?? 0;
  const isEntityDetail = /^\/(?:devices\/cameras|devices|cameras)\/[^/]+\/?$/.test(pathname);
  const isCamerasRoute = pathname === '/cameras' || pathname === '/cameras/';
  const isSettingsRoute = pathname === '/settings' || pathname === '/settings/';
  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const query = search.trim();
    navigate(query ? `/devices?q=${encodeURIComponent(query)}` : '/devices');
  };

  return (
    <div className={`app-shell ${isEntityDetail ? 'app-shell--detail' : ''} ${isCamerasRoute ? 'app-shell--cameras' : ''} ${isSettingsRoute ? 'app-shell--settings' : ''}`}>
      <a className="skip-link" href="#main-content">Перейти к основному содержимому</a>

      <nav className="top-switcher" aria-label="Основные разделы">
        <div className="top-switcher__links">
          {topSwitcherNav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `top-switcher__link ${isActive ? 'is-active' : ''}`}
            >
              <Icon size={24} strokeWidth={1.8} aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          ))}
        </div>
        <NavLink className="top-switcher__search" to="/devices" aria-label="Поиск устройств">
          <Search size={31} strokeWidth={1.8} aria-hidden="true" />
        </NavLink>
      </nav>

      <aside className="sidebar" aria-label="Основная навигация">
        <div className="sidebar__brand">
          <BrandMark />
        </div>
        <nav className="sidebar__nav">
          {desktopNav.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={({ isActive }) => `sidebar-link ${isActive ? 'is-active' : ''}`}>
              <Icon size={19} aria-hidden="true" />
              <span>{label}</span>
              {to === '/events' && openEvents > 0 && <span className="nav-count" aria-label={`${openEvents} новых событий`}>{openEvents}</span>}
            </NavLink>
          ))}
        </nav>
        <NavLink to="/profile" className={({ isActive }) => `sidebar-profile ${isActive ? 'is-active' : ''}`}>
          <span className="avatar avatar--small" aria-hidden="true">{initials(profile?.displayName)}</span>
          <span className="sidebar-profile__copy">
            <strong>{profile?.displayName}</strong>
            <small>{profile ? formatRole(profile.role) : 'Профиль'}</small>
          </span>
          <ChevronDown size={16} aria-hidden="true" />
        </NavLink>
      </aside>

      <header className="desktop-header">
        <form className="global-search" role="search" onSubmit={handleSearch}>
          <Search size={18} aria-hidden="true" />
          <label className="sr-only" htmlFor="global-search">Найти устройство</label>
          <input
            id="global-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Найти устройство или IP…"
          />
          <kbd>Enter</kbd>
        </form>
        <div className="desktop-header__actions">
          <ConnectionBadge state={connectionState} />
          <button className="icon-button" type="button" onClick={toggleTheme} aria-label={resolvedTheme === 'dark' ? 'Включить светлую тему' : 'Включить тёмную тему'}>
            {resolvedTheme === 'dark' ? <Sun size={19} aria-hidden="true" /> : <Moon size={19} aria-hidden="true" />}
          </button>
          <NavLink className="icon-button notification-button" to="/events" aria-label={`События: ${openEvents} новых`}>
            <Bell size={19} aria-hidden="true" />
            {openEvents > 0 && <span aria-hidden="true">{openEvents > 9 ? '9+' : openEvents}</span>}
          </NavLink>
          <NavLink className="header-profile" to="/profile">
            <span className="avatar avatar--small" aria-hidden="true">{initials(profile?.displayName)}</span>
            <span>{profile?.displayName?.split(' ')[0] || 'Профиль'}</span>
          </NavLink>
        </div>
      </header>

      <header className="mobile-header">
        <BrandMark />
        <div className="mobile-header__actions">
          <NavLink className="icon-button notification-button" to="/events" aria-label={`События: ${openEvents} новых`}>
            <Bell size={19} aria-hidden="true" />
            {openEvents > 0 && <span aria-hidden="true">{openEvents > 9 ? '9+' : openEvents}</span>}
          </NavLink>
          <NavLink className="icon-button" to="/settings" aria-label="Настройки">
            <Grid2X2 size={18} aria-hidden="true" />
          </NavLink>
        </div>
      </header>

      <main id="main-content" className="main-content" tabIndex={-1}>
        <div className="page-container">{children}</div>
      </main>

      <nav className="mobile-nav" aria-label="Мобильная навигация">
        {mobileNav.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={({ isActive }) => `mobile-nav__link ${isActive ? 'is-active' : ''}`}>
            <span className="mobile-nav__icon">
              <Icon size={21} aria-hidden="true" />
              {to === '/events' && openEvents > 0 && <span className="mobile-nav__badge" aria-hidden="true">{openEvents > 9 ? '9+' : openEvents}</span>}
            </span>
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
